<template>
  <div>
    <a-card title="进货价明细">
      <div slot="extra">
        <purchase-price-toggle v-if="canViewPurchasePrice" style="margin-right: 12px;" />
        <span>按采购日期 · 含件数 · 需进货价权限</span>
      </div>

      <template v-if="canViewPurchasePrice && showPurchasePrice">
        <a-row gutter="16">
          <a-col :span="24" style="max-width: 220px; margin-bottom: 12px;">
            <a-input
              v-model="searchForm.search"
              placeholder="产品 / 供应商 / 单号"
              allowClear
              @pressEnter="search"
            />
          </a-col>
          <a-col :span="24" style="width: 100px; margin-bottom: 12px;">
            <a-button type="primary" icon="search" @click="search">查询</a-button>
          </a-col>
          <a-col :span="24" style="width: 140px; margin-bottom: 12px;">
            <a-button type="primary" icon="printer" :loading="printLoading" @click="printAll">全量打印</a-button>
            <a-button ref="printBtn" v-print="printConfig" v-show="false">打印</a-button>
          </a-col>
        </a-row>
        <a-table
          size="small"
          rowKey="id"
          :loading="loading"
          :pagination="pagination"
          :dataSource="items"
          :columns="columns"
          @change="tableChange"
        />
      </template>

      <a-result
        v-else-if="canViewPurchasePrice && !showPurchasePrice"
        status="info"
        title="进货价已隐藏"
        sub-title="点击右上角「显示进货价」可再次查看"
      />

      <a-result v-else status="403" title="无权限" sub-title="无查看进货价权限" />
    </a-card>

    <!-- 全量打印内容：移出屏幕但仍参与布局 -->
    <div style="position: absolute; left: -99999px; top: 0; width: 210mm;">
      <div id="printPurchasePriceLedger" style="padding: 8px;">
        <div style="text-align: center; font-size: 18px; font-weight: bold; margin: 4px 0;">进货价明细</div>
        <div v-if="printFilterText" style="font-size: 12px; margin-bottom: 6px;">筛选条件: {{ printFilterText }}</div>
        <table border="1" style="border-collapse: collapse; width: 100%; font-size: 12px;">
          <thead>
            <tr>
              <th style="padding: 3px;">采购日期</th>
              <th style="padding: 3px;">产品</th>
              <th style="padding: 3px;">规格</th>
              <th style="padding: 3px;">进货价</th>
              <th style="padding: 3px;">件数</th>
              <th style="padding: 3px;">金额</th>
              <th style="padding: 3px;">供应商</th>
              <th style="padding: 3px;">采购单号</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in printRows" :key="i">
              <td style="padding: 3px; white-space: nowrap;">{{ r.purchase_date }}</td>
              <td style="padding: 3px;">{{ r.goods_name }}</td>
              <td style="padding: 3px;">{{ r.goods_spec || '' }}</td>
              <td style="padding: 3px; text-align: right; white-space: pre-line;">{{ formatLedgerPrice(r) }}</td>
              <td style="padding: 3px; text-align: right;">{{ r.purchase_quantity }}{{ r.unit_name || '' }}</td>
              <td style="padding: 3px; text-align: right;">{{ r.total_amount }}</td>
              <td style="padding: 3px;">{{ r.supplier_name }}</td>
              <td style="padding: 3px;">{{ r.purchase_order_number }}</td>
            </tr>
            <tr>
              <td colspan="4" style="padding: 3px; text-align: right; font-weight: bold;">合计</td>
              <td style="padding: 3px; text-align: right; font-weight: bold;">{{ printTotalQuantity }}</td>
              <td style="padding: 3px; text-align: right; font-weight: bold;">{{ printTotalAmount }}</td>
              <td colspan="2"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import NP from 'number-precision'
import { goodsPurchasePriceLedger } from '@/api/goods'
import { fetchAllPages } from '@/utils/fetchAll'
import purchasePriceVisibility from '@/mixins/purchasePriceVisibility'

export default {
  name: 'PurchasePriceLedger',
  mixins: [purchasePriceVisibility],
  components: {
    PurchasePriceToggle: () => import('@/components/PurchasePriceToggle/index.vue'),
  },
  data() {
    return {
      loading: false,
      items: [],
      searchForm: { search: '', page: 1, page_size: 16 },
      pagination: { current: 1, total: 0, pageSize: 16 },
      columns: [
        { title: '采购日期', dataIndex: 'purchase_date', width: 110 },
        { title: '产品', dataIndex: 'goods_name', ellipsis: true },
        { title: '规格', dataIndex: 'goods_spec', width: 100, ellipsis: true },
        {
          title: '进货价',
          dataIndex: 'purchase_price',
          width: 130,
          customRender: (value, item) => {
            if (value == null || value === '') return '-'
            const ratio = Number(item.pack_ratio) > 1 ? Number(item.pack_ratio) : 1
            const unit = Math.round(Number(value) * 100) / 100
            if (ratio <= 1) return unit.toFixed(2)
            const pack = Math.round(unit * ratio * 100) / 100
            return `${pack.toFixed(2)}\n(单价 ${unit.toFixed(2)})`
          },
          customCell: () => ({ style: { whiteSpace: 'pre-line', lineHeight: '1.35' } }),
        },
        { title: '件数', dataIndex: 'purchase_quantity', width: 80 },
        { title: '金额', dataIndex: 'total_amount', width: 90 },
        { title: '供应商', dataIndex: 'supplier_name', width: 120, ellipsis: true },
        { title: '采购单号', dataIndex: 'purchase_order_number', width: 140 },
      ],
      printRows: [],
      printTotalQuantity: 0,
      printTotalAmount: 0,
      printFilterText: '',
      printLoading: false,
      printConfig: {
        id: 'printPurchasePriceLedger',
        popTitle: '进货价明细',
        extraHead:
          '<style>@page{size:auto;margin:8mm}html,body{height:auto!important;overflow:visible!important}#printPurchasePriceLedger{height:auto!important;overflow:visible!important}table{width:100%;border-collapse:collapse;page-break-inside:auto}tr{page-break-inside:avoid;page-break-after:auto}thead{display:table-header-group}tfoot{display:table-footer-group}</style>',
      },
    }
  },
  computed: {
    ...mapState({
      isManager: (state) => state.user.isManager,
      permissions: (state) => state.user.permissions || [],
    }),
    purchasePriceAuth() {
      return { isManager: this.isManager, permissions: this.permissions }
    },
  },
  methods: {
    formatLedgerPrice(r) {
      if (r.purchase_price == null || r.purchase_price === '') return '-'
      const ratio = Number(r.pack_ratio) > 1 ? Number(r.pack_ratio) : 1
      const unit = Math.round(Number(r.purchase_price) * 100) / 100
      if (ratio <= 1) return unit.toFixed(2)
      const pack = Math.round(unit * ratio * 100) / 100
      return `${pack.toFixed(2)}\n(单价 ${unit.toFixed(2)})`
    },
    search() {
      this.searchForm.page = 1
      this.pagination.current = 1
      this.list()
    },
    tableChange(pagination) {
      this.pagination.current = pagination.current
      this.searchForm.page = pagination.current
      this.searchForm.page_size = pagination.pageSize
      this.list()
    },
    list() {
      if (!this.canViewPurchasePrice) return
      this.loading = true
      goodsPurchasePriceLedger(this.searchForm)
        .then((data) => {
          this.items = (data && data.results) || []
          this.pagination.total = (data && data.count) || 0
        })
        .catch(() => {
          this.items = []
          this.pagination.total = 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    async printAll() {
      if (!this.canViewPurchasePrice) return
      this.printLoading = true
      try {
        const rows = await fetchAllPages(goodsPurchasePriceLedger, this.searchForm, 200)
        let totalQuantity = 0
        let totalAmount = 0
        rows.forEach((r) => {
          totalQuantity = NP.plus(totalQuantity, Number(r.purchase_quantity) || 0)
          totalAmount = NP.plus(totalAmount, Number(r.total_amount) || 0)
        })
        this.printRows = rows
        this.printTotalQuantity = totalQuantity
        this.printTotalAmount = totalAmount
        this.printFilterText = this.searchForm.search || ''
        if (!rows.length) {
          this.$message.warning('暂无可打印的数据')
          return
        }
        await this.$nextTick()
        setTimeout(() => {
          this.$refs.printBtn && this.$refs.printBtn.$el.click()
        }, 100)
      } catch (e) {
        this.$message.error('获取打印数据失败')
      } finally {
        this.printLoading = false
      }
    },
  },
  mounted() {
    if (this.showPurchasePrice) this.list()
  },
  watch: {
    showPurchasePrice(val) {
      if (val) this.list()
      else {
        this.items = []
        this.pagination.total = 0
      }
    },
  },
}
</script>
